import { action, Action, computed, Computed, thunk, Thunk } from 'easy-peasy';
import { ServiceCheck, DeleteServiceResponse, IssueChatPayload, Message, SaveChatUserPayload, SaveChatUserResponse, SearchUser, Service, ServiceDetails, APIResponse, ServiceCompletePayload, ServiceClosePayload, SearchMaterialResponse, SaveDeleteMaterialResponse, DeleteMaterialPayload, SaveMaterialPayload, Material, ServiceSavePayload } from '~/types';
import { StoreModel } from "~/store";
import { API } from "~/app/routes";
import { CommonModel, getCommonModel } from "~/store/commonStore";
import axios from "axios";
import { REQUIRED_INTERVAL_TIMEUNIT, ServiceStatuses } from '~/app/data/statuses/services';
import { ServiceOfflineService } from '~/sqlite/services/service';

export interface ServiceModel extends CommonModel<Service> {
    filteredServices: Computed<ServiceModel, Service[], StoreModel>
    hasSimplifiedWorkorder: Computed<ServiceModel, boolean, StoreModel>
    hasNextServiceSection: Computed<ServiceModel, boolean, StoreModel>

    showCompleted: boolean
    setShowCompleted: Action<ServiceModel, boolean>

    isWorkOrderFormActive: boolean
    setIsWorkOrderFormActive: Action<ServiceModel, boolean>

    data: ServiceDetails
    fetchService: Thunk<ServiceModel, ServiceDetails['id'], any, StoreModel, Promise<void>>
    fetchedService: Action<ServiceModel, ServiceDetails>
    cancelService: Thunk<ServiceModel, ServiceDetails['id'], any, StoreModel, Promise<DeleteServiceResponse>>
    addChatMessage: Action<ServiceModel, Message>
    addChatUser: Thunk<ServiceModel, IssueChatPayload, any, StoreModel, Promise<SaveChatUserResponse>>

    fetchFavoriteMachinesService: Thunk<ServiceModel, ServiceDetails['id'], any, StoreModel, Promise<void>>
    setOfflineFavoriteMachinesServices: Thunk<ServiceModel, ServiceDetails[], any, StoreModel, Promise<void>>
    fetchOfflineService: Thunk<ServiceModel, ServiceDetails['id'], any, StoreModel, Promise<void>>
    setOfflineService: Thunk<ServiceModel, ServiceDetails, any, StoreModel, Promise<void>>

    serviceChatUserUpdating: boolean
    setServiceChatUserUpdating: Action<ServiceModel, boolean>
    tempChatUsers: SearchUser[]
    setTempChatUsers: Action<ServiceModel, SearchUser[]>
    setChatUsers: Thunk<ServiceModel, SearchUser[]>;
    fetchChatUsers: Thunk<ServiceModel, ServiceDetails['id'], any, StoreModel, Promise<void>>
    saveChatUser: Thunk<ServiceModel, SaveChatUserPayload, any, StoreModel, Promise<SaveChatUserResponse>>

    serviceChecklistUpdating: boolean
    setServiceChecklistUpdating: Action<ServiceModel, boolean>    
    setServiceChecklist: Thunk<ServiceModel, ServiceCheck[]>;
    fetchServiceChecklist: Thunk<ServiceModel, { serviceId: ServiceDetails['id'], serviceIntervalId: ServiceDetails['serviceIntervalId'] }, any, StoreModel, Promise<void>>

    closeService: Thunk<ServiceModel, ServiceClosePayload, any, StoreModel, Promise<APIResponse>>
    completeService: Thunk<ServiceModel, ServiceCompletePayload, any, StoreModel, Promise<APIResponse>>
    saveService: Thunk<ServiceModel, ServiceSavePayload, any, StoreModel, Promise<APIResponse>>

    materialUpdating: boolean
    setMaterialUpdating: Action<ServiceModel, boolean>
    tempMaterials: Material[]
    setTempMaterials: Action<ServiceModel, Material[]>
    setMaterials: Thunk<ServiceModel, Material[]>;
    fetchMaterials: Thunk<ServiceModel, ServiceDetails['id'], any, StoreModel, Promise<void>>
    searchMaterials: Material[]
    searchMaterialsTotal: number
    setSearchedMaterials: Action<ServiceModel, SearchMaterialResponse>
    fetchSearchMaterials: Thunk<ServiceModel, { searchText: string, offset: number }, any, StoreModel, Promise<void>>
    saveMaterial: Thunk<ServiceModel, SaveMaterialPayload, any, StoreModel, Promise<SaveDeleteMaterialResponse>>
    deleteMaterial: Thunk<ServiceModel, DeleteMaterialPayload, any, StoreModel, Promise<SaveDeleteMaterialResponse>>

    isReadOnlyChat: Computed<ServiceModel, boolean, StoreModel>
}

export const serviceModel: ServiceModel = {
    ...getCommonModel<Service>(API.service),

    showCompleted: false,
    setShowCompleted: action((state, payload) => {
        state.showCompleted = payload;
    }),

    isWorkOrderFormActive: false,
    setIsWorkOrderFormActive: action((state, payload) => {
        state.isWorkOrderFormActive = payload;
    }),

    filteredServices: computed(
        [
            state => state.showCompleted,
            (state, storeState) => storeState.machine.data.services
        ],
        (status, services) => services.filter(service =>
            status
            ? (service.status === ServiceStatuses.COMPLETED || service.status === ServiceStatuses.PARTIALLLYCOMPLETED || service.status === ServiceStatuses.CANCELLED)
            : !(service.status === ServiceStatuses.COMPLETED || service.status === ServiceStatuses.PARTIALLLYCOMPLETED || service.status === ServiceStatuses.CANCELLED)
        )
    ),

    hasSimplifiedWorkorder: computed(
        [
            (state, storeState) => storeState.user.data.is_active,
            (state, storeState) => storeState.user.data.departments,
            state => state.data.departmentId,
            state => state.data.hasSimplifiedWorkorderEnabled
        ],
        (is_active, departments, departmentId, hasSimplifiedWorkorderEnabled) => is_active && (departments ?? []).includes(Number(departmentId ?? '-1')) && !!hasSimplifiedWorkorderEnabled
    ),

    hasNextServiceSection: computed(
        [
            state => state.data.serviceProgramType,
            state => state.data.intervalType,
            state => state.data.timeUnit,
            state => state.data.primaryMeterUnit,
            state => state.data.scheduleAt
        ],
        (serviceProgramType, intervalType, timeUnit, primaryMeterUnit, scheduleAt)  => serviceProgramType !== undefined && serviceProgramType !== 0 && intervalType !== 1 && timeUnit !== REQUIRED_INTERVAL_TIMEUNIT && (primaryMeterUnit !== null || scheduleAt !== null)
    ),
    
    data: {
        id: '',
        unitText: '',
        serviceOccurrenceText: '',
        intervalTypeText: '',
        status: ServiceStatuses.PREVIOUS,
        machineId: '',
        machineIntern: '',
        machineName: '',
        statusDate: '',
        departmentId: '',
        departmentName: null,
        departmentPrefix: '',
        workOrderNo: '',
        workDetails: [],
        chat: [],
        chatUsers: [],
        checklist: [],
        createdAt: '',
        updatedAt: '',
        serviceCreatedBy: null,
        serviceUpdatedBy: null,
        projectId: null,
        registrationDate: '',
        dueHours: '',
        serviceStatus: undefined,
        serviceProgramName: '',
        dateDiff: 0,
        materials: [],
        serviceIntervalId: '',
        workOrderId: null,
        scheduleAt: null,
        primaryMeterUnit: null,
        intervalType: null,
        serviceProgramType: null,
        timeUnit: null,
        operatingTime: 0,
        secondaryMeterUnit: null,
        alertScheduleDays: 0,
        alertServiceOccurrence: 0
    },

    fetchService: thunk((actions, payload, { getStoreActions }) => {
        return axios.get<ServiceDetails, ServiceDetails>(`${API.service}/${payload}`)
            .then(data => {
                actions.fetchedService(data);
                actions.setOfflineService(data);
                getStoreActions().serviceCheck.loadChecksData({checks: data.checklist});
            });
    }),
    fetchedService: action((state, payload) => {
        state.data = payload;
    }),
    fetchFavoriteMachinesService: thunk((actions, payload) => {
        return axios.get<ServiceDetails, ServiceDetails[]>(`${API.service}/${payload}`)
            .then(data => {
                actions.setOfflineFavoriteMachinesServices(data);
            });
    }),

    setOfflineFavoriteMachinesServices: thunk(async (actions, payload) => {
        const ServiceOffline = new ServiceOfflineService();
        await ServiceOffline.reset(payload);
    }),
    fetchOfflineService: thunk(async (actions, payload) => {
        const ServiceOffline = new ServiceOfflineService();
        let service = await ServiceOffline.getService(payload);
        service && actions.fetchedService(service);
    }),
    setOfflineService: thunk(async (actions, payload) => {
        const ServiceOffline = new ServiceOfflineService();
        ServiceOffline.updateServiceDetail(payload);
    }),
    cancelService:  thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.post<null, DeleteServiceResponse>(`${API.service}/cancel`, {id: payload}).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setFetching(false);
        })
    }),

    serviceChatUserUpdating: false,
    setServiceChatUserUpdating: action((state, payload) => {
        state.serviceChatUserUpdating = payload;
    }),
    tempChatUsers: [],
    setTempChatUsers: action((state, payload) => {
      state.tempChatUsers = payload;
    }),
    setChatUsers: thunk(async (actions, payload, { getState }) => {
        const state = getState();
        state.data.chatUsers = payload;
        state.tempChatUsers = payload;
        await actions.setOfflineService(state.data);
    }),
    fetchChatUsers: thunk((actions, payload) => {
        if (String(payload ?? '').trim() === '') {
            return Promise.resolve();
        }
        return axios.post<null, SearchUser[]>(`${API.serviceUsers}/fetch/${payload}`)
            .then(
                data => {
                    actions.setChatUsers(data);
                })
    }),
    saveChatUser: thunk((actions, payload) => {
        actions.setServiceChatUserUpdating(true);
        return axios.post<null, SaveChatUserResponse>(`${API.serviceUsers}/save`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setServiceChatUserUpdating(false);
        })
    }),
    addChatUser: thunk((actions, payload) => {
        return axios.post<null, SaveChatUserResponse>(`${API.serviceUsers}/add`, payload).then(data => { return data; })
    }),
    addChatMessage: action((state, payload) => {
        Number(state.data.id) === Number(payload.issueId) && state.data.chat.push(payload);
    }),

    serviceChecklistUpdating: false,
    setServiceChecklistUpdating: action((state, payload) => {
        state.serviceChecklistUpdating = payload;
    }),
    setServiceChecklist: thunk(async (actions, payload, { getState }) => {
        const state = getState();
        state.data.checklist = payload;
        await actions.setOfflineService(state.data);
    }),
    fetchServiceChecklist: thunk((actions, payload) => {
        return axios.post<null, ServiceCheck[]>(`${API.service}/checklist`, payload)
            .then(
                data => {
                    actions.setServiceChecklist(data);
                })
    }),

    closeService: thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.post<null, APIResponse>(`${API.service}/close`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setFetching(false);
        })
    }),
    completeService: thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.post<null, APIResponse>(`${API.service}/complete`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setFetching(false);
        })
    }),
    saveService: thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.post<null, APIResponse>(`${API.service}/save`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setFetching(false);
        })
    }),

    materialUpdating: false,
    setMaterialUpdating: action((state, payload) => {
        state.materialUpdating = payload;
    }),
    tempMaterials: [],
    setTempMaterials: action((state, payload) => {
        state.tempMaterials = payload;
    }),
    setMaterials: thunk(async (actions, payload, { getState }) => {
        const state = getState();
        state.data.materials = payload;
        state.tempMaterials = payload;
        await actions.setOfflineService(state.data);
    }),
    fetchMaterials: thunk((actions, payload) => {
        return axios.post<null, Material[]>(`${API.serviceMaterial}/fetch/${payload}`)
            .then(
                data => {
                    actions.setMaterials(data);
                })
    }),
    searchMaterials: [],
    searchMaterialsTotal: 0,
    setSearchedMaterials: action((state, payload) => {
        state.searchMaterials = payload.materials;
        state.searchMaterialsTotal = payload.total;
    }),
    fetchSearchMaterials: thunk((actions, payload) => {
        actions.setMaterialUpdating(true);
        return axios.post<null, SearchMaterialResponse>(`${API.serviceMaterial}/search`, payload).then(
            data => {
                actions.setSearchedMaterials(data);
            }
        ).finally(() => {
            actions.setMaterialUpdating(false);
        })
    }),
    saveMaterial: thunk((actions, payload) => {
        actions.setMaterialUpdating(true);
        return axios.post<null, SaveDeleteMaterialResponse>(`${API.serviceMaterial}/save`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setMaterialUpdating(false);
        })
    }),
    deleteMaterial: thunk((actions, payload) => {
        actions.setMaterialUpdating(true);
        return axios.post<null, SaveDeleteMaterialResponse>(`${API.serviceMaterial}/delete`, payload).then(
            data => {
                return data;
            }
        ).finally(() => {
            actions.setMaterialUpdating(false);
        })
    }),

    isReadOnlyChat: computed(
        [state => state.data.status],
        (status) => false
    ),
};
